import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Check, ChevronDown, Pencil, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  type BulkPayload,
  type Registration,
  type RegistrationCategory,
  type RegistrationTeam,
} from '../../services/registrations';
import YesNoSwitch from './YesNoSwitch';

// The dropdown primitives are unstyled here (see components/ui/dropdown-menu),
// so the popover surface is spelled out, as GymSwitcher does.
const CONTENT_CLASS =
  'z-50 max-h-[60svh] min-w-52 overflow-y-auto rounded-lg border bg-popover p-1 text-popover-foreground shadow-md';

const ITEM_CLASS =
  'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground';

interface RegistrationsTableProps {
  /** The category this table stands for; null is the "not sorted yet" table. */
  categoryId: string | null;
  title: string;
  registrations: Registration[];
  categories: RegistrationCategory[];
  teams: RegistrationTeam[];
  /** Whether this viewer may still change anything at all. */
  deskOpen: boolean;
  /** A column for the gender of each row's class: the organiser's agreement covers it. */
  showGender?: boolean;
  /** A column for each row's turn: some class here is split over two or more. */
  showTurn?: boolean;
  /** The "paid" column and its bulk action: the competition takes an entry fee. */
  showPaid?: boolean;
  emptyLabel: string;
  onBulk: (ids: string[], attributes: BulkPayload) => Promise<void>;
  onTogglePaid: (registration: Registration) => Promise<void>;
  onToggleConfirmed: (registration: Registration) => Promise<void>;
  onEdit: (registration: Registration) => void;
}

/**
 * One category's start list, and the header that works on it.
 *
 * A desk sorts a competition in batches, not row by row: everyone who turned
 * up from the same squad, everyone whose club paid together, everyone who was
 * entered without a category and has to land in one. So the selection and the
 * things the header can do to it live here, one copy per table — ticking rows
 * under "Junior" can only ever act on Junior's rows.
 */
export default function RegistrationsTable({
  categoryId,
  title,
  registrations,
  categories,
  teams,
  deskOpen,
  showGender = false,
  showTurn = false,
  showPaid = false,
  emptyLabel,
  onBulk,
  onTogglePaid,
  onToggleConfirmed,
  onEdit,
}: RegistrationsTableProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);

  // Rows move between tables as they are sorted, so what is ticked is read back
  // from the rows actually here rather than trusted from the set.
  const selectedIds = useMemo(
    () => registrations.filter((r) => selected.has(r.id)).map((r) => r.id),
    [registrations, selected]
  );

  const allSelected = registrations.length > 0 && selectedIds.length === registrations.length;
  const paidCount = registrations.filter((r) => r.paid).length;
  const confirmedCount = registrations.filter((r) => r.confirmed).length;
  const canAct = deskOpen && selectedIds.length > 0 && !applying;

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(registrations.map((r) => r.id)));

  const toggleOne = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = async (attributes: BulkPayload) => {
    if (selectedIds.length === 0) return;
    setApplying(true);
    try {
      await onBulk(selectedIds, attributes);
      // Whatever just happened, the rows are no longer what the desk was
      // pointing at — a category change moved them out of this table entirely.
      setSelected(new Set());
    } catch {
      // The page has already said what went wrong. The ticks stay put so the
      // same batch can be tried again rather than picked out a second time.
    } finally {
      setApplying(false);
    }
  };

  // Moving to the category the rows are already in is not a move. Emptying the
  // category out again is, so a table with nowhere else to send people still
  // offers "no category".
  const targetCategories = categories.filter((category) => category.id !== categoryId);
  const canMove = targetCategories.length > 0 || categoryId !== null;
  // The turns this table's class is split over, where the desk books them:
  // the ticked rows go on one of them, or back to "turn to choose". A full
  // turn is still offered — the limit is what athletes book against, and the
  // desk makes the exceptions.
  const turns = categories.find((category) => category.id === categoryId)?.turns ?? [];

  return (
    <Card data-testid="registration-table" data-category={categoryId ?? 'none'}>
      <CardHeader className="gap-3 space-y-0 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {deskOpen && (
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
                disabled={registrations.length === 0}
                aria-label={t('gymAdmin.registrations.selectAll')}
              />
            )}
            <span className="font-medium">{title}</span>
            <span className="text-xs text-muted-foreground">
              {t(showPaid ? 'gymAdmin.registrations.groupSummary' : 'gymAdmin.registrations.groupSummaryNoPayments', {
                total: registrations.length,
                paid: paidCount,
                confirmed: confirmedCount,
              })}
            </span>
          </div>

          {deskOpen && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {t('gymAdmin.registrations.selectedCount', { total: selectedIds.length })}
              </span>

              {canMove && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={!canAct}>
                    <Button variant="outline" size="sm" className="gap-1">
                      {t('gymAdmin.registrations.bulkCategory')}
                      <ChevronDown className="size-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className={CONTENT_CLASS}>
                    <DropdownMenuLabel className="px-2 py-1 text-xs text-muted-foreground">
                      {t('gymAdmin.registrations.bulkCategory')}
                    </DropdownMenuLabel>
                    {categoryId !== null && (
                      <DropdownMenuItem
                        className={ITEM_CLASS}
                        onSelect={() => apply({ competition_category_id: null })}
                      >
                        {t('gymAdmin.registrations.noCategory')}
                      </DropdownMenuItem>
                    )}
                    {targetCategories.map((category) => (
                      <DropdownMenuItem
                        key={category.id}
                        className={ITEM_CLASS}
                        onSelect={() => apply({ competition_category_id: category.id })}
                      >
                        {category.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {turns.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={!canAct}>
                    <Button variant="outline" size="sm" className="gap-1" data-testid="bulk-turn">
                      {t('gymAdmin.registrations.bulkTurn')}
                      <ChevronDown className="size-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className={CONTENT_CLASS}>
                    <DropdownMenuLabel className="px-2 py-1 text-xs text-muted-foreground">
                      {t('gymAdmin.registrations.bulkTurn')}
                    </DropdownMenuLabel>
                    {turns.map((turn) => (
                      <DropdownMenuItem
                        key={turn.id}
                        className={ITEM_CLASS}
                        onSelect={() => apply({ competition_round_id: turn.id })}
                        data-testid={`bulk-turn-${turn.id}`}
                      >
                        <CalendarClock className="size-4" />
                        {turn.name}
                        {turn.full ? (
                          <span className="ml-auto text-xs text-amber-600">{t('stageEntry.dialog.full')}</span>
                        ) : turn.seats_left != null ? (
                          <span className="ml-auto text-xs text-muted-foreground">
                            {t('stageEntry.dialog.seatsLeft', { count: turn.seats_left })}
                          </span>
                        ) : null}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem
                      className={ITEM_CLASS}
                      onSelect={() => apply({ competition_round_id: null })}
                      data-testid="bulk-turn-none"
                    >
                      {t('gymAdmin.registrations.turnToChoose')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {teams.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={!canAct}>
                    <Button variant="outline" size="sm" className="gap-1">
                      {t('gymAdmin.registrations.bulkTeam')}
                      <ChevronDown className="size-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className={CONTENT_CLASS}>
                    <DropdownMenuItem
                      className={ITEM_CLASS}
                      onSelect={() => apply({ competition_team_id: null })}
                    >
                      {t('gymAdmin.members.fields.noTeam')}
                    </DropdownMenuItem>
                    {teams.map((team) => (
                      <DropdownMenuItem
                        key={team.id}
                        className={ITEM_CLASS}
                        onSelect={() => apply({ competition_team_id: team.id })}
                      >
                        {team.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {showPaid && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={!canAct}>
                    <Button variant="outline" size="sm" className="gap-1">
                      {t('gymAdmin.registrations.bulkPayment')}
                      <ChevronDown className="size-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className={CONTENT_CLASS}>
                    <DropdownMenuItem className={ITEM_CLASS} onSelect={() => apply({ paid: true })}>
                      <Check className="size-4" />
                      {t('gymAdmin.registrations.markPaid')}
                    </DropdownMenuItem>
                    <DropdownMenuItem className={ITEM_CLASS} onSelect={() => apply({ paid: false })}>
                      <X className="size-4" />
                      {t('gymAdmin.registrations.markUnpaid')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Confirming is what puts people on the scoreboard, and a club
                  turns up together. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={!canAct}>
                  <Button variant="outline" size="sm" className="gap-1">
                    {t('gymAdmin.registrations.bulkConfirmation')}
                    <ChevronDown className="size-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={CONTENT_CLASS}>
                  <DropdownMenuItem
                    className={ITEM_CLASS}
                    onSelect={() => apply({ confirmed: true })}
                    data-testid="bulk-confirm"
                  >
                    <Check className="size-4" />
                    {t('gymAdmin.registrations.markConfirmed')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={ITEM_CLASS}
                    onSelect={() => apply({ confirmed: false })}
                    data-testid="bulk-unconfirm"
                  >
                    <X className="size-4" />
                    {t('gymAdmin.registrations.markUnconfirmed')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {registrations.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {/* Ticking rows is only worth anything while there is
                    something the header can do with them. */}
                {deskOpen && <TableHead className="w-8" />}
                <TableHead>{t('gymAdmin.members.fields.name')}</TableHead>
                <TableHead>{t('gymAdmin.members.fields.surname')}</TableHead>
                <TableHead>{t('gymAdmin.members.fields.team')}</TableHead>
                {showGender && <TableHead className="w-20">{t('gymAdmin.registrations.gender')}</TableHead>}
                <TableHead className="w-20">{t('gymAdmin.registrations.birthYear')}</TableHead>
                {showTurn && <TableHead>{t('gymAdmin.registrations.turn')}</TableHead>}
                {showPaid && <TableHead className="w-24">{t('gymAdmin.registrations.paid')}</TableHead>}
                <TableHead className="w-24">{t('gymAdmin.registrations.confirmed')}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {registrations.map((registration) => (
                <TableRow
                  key={registration.id}
                  data-testid="registration-row"
                  data-state={selected.has(registration.id) ? 'selected' : undefined}
                >
                  {deskOpen && (
                    <TableCell>
                      <Checkbox
                        checked={selected.has(registration.id)}
                        onCheckedChange={() => toggleOne(registration.id)}
                        aria-label={t('gymAdmin.registrations.selectRow', {
                          name: registration.full_name,
                        })}
                      />
                    </TableCell>
                  )}

                  <TableCell className="font-medium">
                    <span className="flex items-center gap-1.5">
                      {registration.name || registration.full_name}
                      {/* Under the consent age: nobody was invited for this
                          row, whatever address is on it. */}
                      {registration.minor && (
                        <Badge
                          variant="outline"
                          className="shrink-0 font-normal"
                          title={
                            registration.managed
                              ? t('gymAdmin.registrations.managed')
                              : t('gymAdmin.registrations.minorHint')
                          }
                        >
                          {t('gymAdmin.registrations.minor')}
                        </Badge>
                      )}
                      {/* Typed in by a squad's team manager from the
                          competition's page: may carry no address at all. Or
                          picked from the squad's athletes at another stage,
                          whose details came from that entry. */}
                      {registration.team_entered_at && (
                        <Badge
                          variant="outline"
                          className="shrink-0 font-normal"
                          data-testid="team-entry-badge"
                          title={t(
                            registration.team_reentered
                              ? 'gymAdmin.registrations.teamReentryHint'
                              : 'gymAdmin.registrations.teamEntryHint',
                            {
                              name: registration.team_entered_by || t('gymAdmin.registrations.teamEntryUnknown'),
                              date: new Date(registration.team_entered_at).toLocaleDateString(),
                            }
                          )}
                        >
                          {t('gymAdmin.registrations.teamEntry')}
                        </Badge>
                      )}
                    </span>
                  </TableCell>

                  <TableCell>{registration.surname || t('common.notSet')}</TableCell>
                  <TableCell>{registration.team_name || t('common.notSet')}</TableCell>
                  {showGender && (
                    <TableCell data-testid="registration-gender">
                      {registration.gender ? (
                        <span title={t(`gymAdmin.registrations.genders.${registration.gender}`)}>
                          {t(`gymAdmin.registrations.genderShort.${registration.gender}`)}
                        </span>
                      ) : (
                        t('common.notSet')
                      )}
                    </TableCell>
                  )}
                  <TableCell>{registration.birth_date?.slice(0, 4) || t('common.notSet')}</TableCell>
                  {/* The turn booked, or — on a class split over several with
                      none booked — the to-do the filter above narrows to. */}
                  {showTurn && (
                    <TableCell data-testid="registration-turn">
                      {registration.competition_round_name ? (
                        registration.competition_round_name
                      ) : registration.turn_to_choose ? (
                        <Badge
                          variant="outline"
                          className="border-amber-500/50 font-normal text-amber-700 dark:text-amber-400"
                          data-testid="turn-to-choose-badge"
                        >
                          {t('gymAdmin.registrations.turnToChooseBadge')}
                        </Badge>
                      ) : (
                        t('common.notSet')
                      )}
                    </TableCell>
                  )}

                  {/* The two things the desk flips on the morning: the money,
                      where there is a fee, and whether this person is really
                      climbing — which is what lets the judges score them. */}
                  {showPaid && (
                    <TableCell>
                      <YesNoSwitch
                        checked={registration.paid}
                        disabled={!deskOpen}
                        onToggle={() => onTogglePaid(registration)}
                        aria-label={t('gymAdmin.registrations.paid')}
                        data-testid="paid-switch"
                      />
                    </TableCell>
                  )}

                  <TableCell>
                    <YesNoSwitch
                      checked={registration.confirmed}
                      disabled={!deskOpen}
                      onToggle={() => onToggleConfirmed(registration)}
                      aria-label={t('gymAdmin.registrations.confirmed')}
                      data-testid="confirmed-switch"
                    />
                  </TableCell>

                  <TableCell>
                    {registration.editable && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(registration)}
                        title={t('gymAdmin.registrations.editEntrant')}
                      >
                        <Pencil className="size-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
